import { useEffect, useState } from "react";
import { Control, useWatch } from "react-hook-form";
import { CarbonCopies, ModuSignStartDocumentRequest } from "src/api/contract/modusign-types";
import { BaseButton, BaseInput, BaseSelect } from "src/components";
import { formatPhoneNumber, INVALID_PHONE_NUMBER_MESSAGE, isPhoneNumber, isValidKoreanMobile, validateEmail } from "src/utils";
import { SigningMethodOptions } from "../modusign-constants";

type Props = {
  control: Control<ModuSignStartDocumentRequest>;
  onClose: () => void;
  onSubmit: (carbonCopies: CarbonCopies[]) => void;
};

// 언어 옵션
const LanguageOptions = [
  { label: "한국어", value: "ko" },
  { label: "영어", value: "en" },
  { label: "중국어", value: "zh-CN" },
  { label: "일본어", value: "ja" },
  { label: "베트남어", value: "vi" },
];

type CarbonCopy = {
  signingMethod: string;
  contact: string;
  locale: string;
  errorText?: string;
};

const ModuSignExternalReference = ({ control, onClose, onSubmit }: Props) => {
  const carbonCopiesList = useWatch({ control, name: "document.carbonCopiesList" }) || [];
  const [carbonCopies, setCarbonCopies] = useState<CarbonCopy[]>([
    {
      signingMethod: "EMAIL",
      contact: "",
      locale: "ko",
    },
  ]);
  console.log("carbonCopiesList", carbonCopiesList);
  console.log("carbonCopies", carbonCopies);

  useEffect(() => {
    if (carbonCopiesList.length > 0) {
      setCarbonCopies(
        carbonCopiesList.map((cop) => ({
          // 저장된 값이 전화번호인지 이메일인지 구분하는 분기일 뿐이므로 기존 판정(isPhoneNumber)을 유지한다.
          // isValidKoreanPhone(max) 으로 바꾸면 "010-0123-4567" 같은 기존 데이터가 KAKAO -> EMAIL 로 뒤집힌다.
          signingMethod: isPhoneNumber(cop.contact) ? "KAKAO" : "EMAIL",
          contact: cop.contact,
          locale: cop.locale,
        })),
      );
    }
  }, [carbonCopiesList]);

  // 실시간 validation 함수
  const validateSingleContact = (signingMethod: string, contact: string): string | undefined => {
    if (!contact) {
      return "";
    }
    if (signingMethod === "EMAIL" && !validateEmail(contact)) {
      return "올바른 이메일 형식이 아닙니다.";
    }
    if (signingMethod === "KAKAO" && !isValidKoreanMobile(contact)) {
      return INVALID_PHONE_NUMBER_MESSAGE;
    }
    return undefined;
  };

  // contact 값 변경 시 실시간 validation
  const handleContactChange = (index: number, value: string) => {
    setCarbonCopies(
      carbonCopies.map((c, i) => {
        if (i === index) {
          const errorText = validateSingleContact(c.signingMethod, value);
          return { ...c, contact: value, errorText };
        }
        return c;
      }),
    );
  };

  // 서명 방법 변경 시 기존 contact 재검증
  const handleSigningMethodChange = (index: number, signingMethod: string) => {
    setCarbonCopies(
      carbonCopies.map((c, i) => {
        if (i === index) {
          const errorText = validateSingleContact(signingMethod, c.contact);
          return { ...c, signingMethod, errorText };
        }
        return c;
      }),
    );
  };

  return (
    <>
      <section className="base-abstract-modal__title">
        <h1>외부 참조자 설정</h1>
      </section>
      <section className="base-abstract-modal__contents mx30">
        <ul className="base-list">
          <li>외부 참조자는 문서가 전송되거나 완료될 때에 알림을 받습니다.</li>
          <li>외부참조자에게 휴대폰 번호 또는 이메일로 알림해 주세요.</li>
        </ul>
        <div className="contents-container mt20">
          {carbonCopies.map((carbonCopy, index) => (
            <section className="contents-container__grid" key={"carbonCopy-" + index}>
              <div className="contents-container__grid-index">
                <p className="required">외부참조자 {index + 1}</p>
              </div>
              <div className="contents-container__grid-contents">
                <div>
                  <div className="flex-center mb10">
                    <BaseSelect
                      menuPosition="fixed"
                      className="minmax100 mr10"
                      stateOptions={SigningMethodOptions}
                      value={carbonCopy.signingMethod}
                      setStateValue={(value: string) => handleSigningMethodChange(index, value)}
                    />
                    <BaseSelect
                      menuPosition="fixed"
                      className="minmax120"
                      stateOptions={LanguageOptions}
                      value={carbonCopy.locale}
                      setStateValue={(value: string) => setCarbonCopies(carbonCopies.map((c, i) => (i === index ? { ...c, locale: value } : c)))}
                    />
                  </div>
                  <div className="flex-row flex-center-start">
                    <BaseInput
                      name={`document.carbonCopiesList.${index}.contact`}
                      placeholder={`${carbonCopy.signingMethod === "EMAIL" ? "이메일" : "휴대폰"}을 입력해주세요`}
                      value={carbonCopy.contact}
                      className="minmax240"
                      errorText={carbonCopy.errorText}
                      onChange={(value: string) =>
                        handleContactChange(index, carbonCopy.signingMethod === "EMAIL" ? value : formatPhoneNumber(value))
                      }
                    />
                    <div>
                      <button
                        className="minus-btn ml10"
                        // disabled={!canDelete}

                        onClick={(e) => {
                          e.preventDefault();
                          setCarbonCopies(carbonCopies.filter((_, i) => i !== index));
                        }}
                      />
                      <button
                        className="plus-btn ml10"
                        onClick={(e) => {
                          e.preventDefault();
                          setCarbonCopies([...carbonCopies, { signingMethod: "EMAIL", contact: "", locale: "ko" }]);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </section>
          ))}
        </div>
      </section>
      <section className="base-abstract-modal__btn-wrap">
        <BaseButton title="취소" className="color-white" onClick={onClose} />
        <BaseButton
          title="확인"
          disabled={carbonCopies.some((cop) => cop.contact === "")}
          onClick={() => {
            // 모든 validation이 통과한 경우에만 submit
            if (!carbonCopies.some((cop) => cop.contact === "" || cop.errorText)) {
              const request = carbonCopies.map((cop) => ({
                contact: cop.contact,
                locale: cop.locale,
              }));

              onSubmit(request);
            }
          }}
        />
      </section>
    </>
  );
};

export default ModuSignExternalReference;
